/**
 * Floodlight on the original screens (v1.71, Trev Oct 19: keep our layout,
 * background and glass; Floodlight changes type and UX/UI polish only).
 *
 * Home, sign in, register, forgot / reset password, admin login and the
 * waitlist render the SAME component in both looks. Each screen keeps its one
 * raw style object and builds two sheets from it:
 *
 *   const SHEETS = lookSheets(RAW, { /* Floodlight-only overrides *\/ });
 *   const styles = useLookStyles(SHEETS);
 *
 * Classic gets StyleSheet.create(RAW), exactly what it rendered before
 * (pixel-identical). Floodlight gets the same styles retyped in Archivo (every
 * text style except the ClimateCrisis wordmark, which keeps its character)
 * plus the per-key polish overrides.
 *
 * `useOriginalPolish()` adds the web-only polish layer: a `data-fl-orig`
 * attribute for the screen root (visible keyboard focus ring, pointer
 * cursors, Archivo inside shared inputs such as SearchInput) and whether
 * motion should be skipped (Floodlight + OS reduced motion; Classic keeps
 * its original animations untouched).
 */
import { StyleSheet } from 'react-native';
import { useUiStyle } from '../context/UiStyleContext';
import { FONT as ARCHIVO } from '../components/floodlight/theme';
import injectCss from '../utils/injectCss';
import { IS_WEB, useReducedMotion } from '../utils/motion';

export { ARCHIVO };
export const WORDMARK_FONT = 'ClimateCrisis';
/** Focus ring / hover tint: the light purple accent. */
export const FL_RING = '#A78BFA';
/** Primary fill for Floodlight CTAs (purple → blue). */
export const FL_ACCENT_GRADIENT = ['#7C3AED', '#4F46E5', '#2563EB'];

const isTextStyle = (s) => s && typeof s === 'object' && ('fontSize' in s || 'fontFamily' in s);

/** One style object retyped: Archivo for every text style except the wordmark. */
export function retype(style) {
    if (!ARCHIVO || !isTextStyle(style) || style.fontFamily === WORDMARK_FONT) return style;
    return { ...style, fontFamily: ARCHIVO };
}

/** The Floodlight overrides merged in per key, then every text style retyped. */
export function floodlightType(raw, overrides = {}) {
    const out = { ...raw };
    Object.keys(overrides).forEach((k) => { out[k] = { ...(out[k] || {}), ...overrides[k] }; });
    Object.keys(out).forEach((k) => { out[k] = retype(out[k]); });
    return out;
}

/** `{ classic, floodlight }` sheets from one raw style object. */
export function lookSheets(raw, overrides) {
    return { classic: StyleSheet.create(raw), floodlight: StyleSheet.create(floodlightType(raw, overrides)) };
}

export function useLookStyles(sheets) {
    const { isFloodlight } = useUiStyle();
    return isFloodlight ? sheets.floodlight : sheets.classic;
}

export const ORIGINAL_POLISH_CSS = `
[data-fl-orig] input, [data-fl-orig] textarea { font-family: ${ARCHIVO} !important; }
[data-fl-orig] input::placeholder, [data-fl-orig] textarea::placeholder { font-family: ${ARCHIVO} !important; -webkit-text-fill-color: currentColor !important; }
[data-fl-orig] [role="button"], [data-fl-orig] [role="link"], [data-fl-orig] [role="checkbox"] { cursor: pointer; }
[data-fl-orig] [aria-disabled="true"] { cursor: default; }
[data-fl-orig] :focus { outline: none; }
[data-fl-orig] [role="button"]:focus-visible, [data-fl-orig] [role="link"]:focus-visible,
[data-fl-orig] [role="checkbox"]:focus-visible, [data-fl-orig] a:focus-visible, [data-fl-orig] [tabindex="0"]:focus-visible {
  outline: 2px solid ${FL_RING} !important; outline-offset: 3px; }
@media (prefers-reduced-motion: reduce) {
  [data-fl-orig], [data-fl-orig] * { transition-duration: 0ms !important; scroll-behavior: auto !important; }
}
`;

/**
 * `{ isFloodlight, still, root }`: spread `root` on the screen's outer View
 * (empty in Classic); `still` = skip entrance / ambient motion.
 */
export function useOriginalPolish() {
    const { isFloodlight } = useUiStyle();
    const reduced = useReducedMotion();
    if (isFloodlight && IS_WEB) injectCss('fl-original-polish', ORIGINAL_POLISH_CSS);
    return {
        isFloodlight,
        still: isFloodlight && reduced,
        root: isFloodlight ? { dataSet: { flOrig: '' } } : {},
    };
}

/**
 * Shared Floodlight refinements for the glass auth card (sign in, register).
 * Keys a screen doesn't have are simply added and never read.
 */
const PRIMARY_TEXT = { fontWeight: '700', letterSpacing: 0.3 };
export const AUTH_FL_OVERRIDES = {
    brandTagline: { fontWeight: '600', letterSpacing: 2.4, color: 'rgba(255,255,255,0.55)' },
    formCard: { borderRadius: 20 },
    welcomeTitle: { fontWeight: '700', letterSpacing: -0.6 },
    welcomeSubtitle: { color: 'rgba(255,255,255,0.62)' },
    inputLabel: { fontSize: 11, fontWeight: '600', letterSpacing: 1.1, color: 'rgba(255,255,255,0.58)' },
    inputContainer: { backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: 12, minHeight: 46, justifyContent: 'center' },
    inputContainerHovered: { borderColor: 'rgba(255,255,255,0.22)' },
    inputContainerFocused: {
        borderColor: 'rgba(167,139,250,0.75)',
        backgroundColor: 'rgba(124,58,237,0.06)',
        ...(IS_WEB ? { boxShadow: '0 0 0 3px rgba(124,58,237,0.22)' } : {}),
    },
    inputContainerInvalid: { borderColor: 'rgba(248,113,113,0.55)' },
    passwordToggle: { padding: 8, marginVertical: -6, marginRight: -4, borderRadius: 8 },
    signInButton: { borderRadius: 12 },
    primaryButton: { borderRadius: 12 },
    buttonGradient: { paddingVertical: 15, borderRadius: 11 },
    primaryHover: IS_WEB ? { borderColor: 'rgba(167,139,250,0.7)', boxShadow: '0 10px 28px rgba(79,70,229,0.32)' } : {},
    signInButtonText: PRIMARY_TEXT,
    primaryButtonText: PRIMARY_TEXT,
    googleButton: { borderRadius: 12 },
    googleButtonGradient: { borderRadius: 11 },
    googleButtonText: { fontWeight: '600' },
    dividerText: { fontSize: 11, fontWeight: '600', letterSpacing: 1.4, textTransform: 'uppercase' },
    statusText: { borderRadius: 10 },
    linkPressable: { minHeight: 40, justifyContent: 'center' },
};
